/**
 * Nearby Services Widget
 * 
 * Shows nearby gas stations, food, lodging, medical, etc.
 * Essential for oilfield workers in remote areas
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Rating,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { telegram } from '../services/telegram';
import {
  LocalGasStation as GasIcon,
  Restaurant as FoodIcon,
  Hotel as LodgingIcon,
  LocalHospital as MedicalIcon,
  Build as RepairIcon,
  Store as ConvenienceIcon,
  Atm as AtmIcon,
  Navigation as NavIcon,
  AccessTime as OpenIcon,
  Close as CloseIcon,
  Storefront as ServicesIcon,
} from '@mui/icons-material';
import { useTranslation } from '../i18n/translations';

interface Place {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  distance: number;
  distanceDisplay: string;
  rating?: number;
  totalRatings?: number;
  isOpen?: boolean;
  isOperational: boolean;
  icon: string;
  category: string;
}

interface NearbyServicesResponse {
  places: Place[];
  count: number;
  category: string;
  categoryIcon: string;
}

interface NearbyServicesWidgetProps {
  latitude: number;
  longitude: number;
  darkMode?: boolean;
  open: boolean;
  onClose: () => void;
}

const CATEGORIES = [
  { key: 'fuel', labelKey: 'nearbyGasStations', icon: GasIcon, color: '#f59e0b' },
  { key: 'food', labelKey: 'nearbyRestaurants', icon: FoodIcon, color: '#ef4444' },
  { key: 'lodging', labelKey: 'nearbyHotels', icon: LodgingIcon, color: '#8b5cf6' },
  { key: 'medical', labelKey: 'nearbyMedical', icon: MedicalIcon, color: '#ec4899' },
  { key: 'repair', labelKey: 'nearbyAutoRepair', icon: RepairIcon, color: '#6b7280' },
  { key: 'convenience', labelKey: 'nearbyStores', icon: ConvenienceIcon, color: '#10b981' },
  { key: 'atm', labelKey: 'nearbyATMs', icon: AtmIcon, color: '#3b82f6' },
] as const;

// Radius options in miles with meter conversion
const RADIUS_OPTIONS = [
  { miles: 5, meters: 8047, label: '5 mi' },
  { miles: 10, meters: 16093, label: '10 mi' },
  { miles: 50, meters: 80467, label: '50 mi' },
  { miles: 100, meters: 160934, label: '100 mi' },
  { miles: 150, meters: 241402, label: '150 mi' },
  { miles: 200, meters: 321869, label: '200 mi' },
];

export function NearbyServicesWidget({ latitude, longitude, darkMode = true, open, onClose }: NearbyServicesWidgetProps) {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('fuel');
  const [selectedRadius, setSelectedRadius] = useState(50); // Default 50 miles
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!latitude || !longitude || !open) return;

    const fetchPlaces = async () => {
      setLoading(true);
      setError(null);

      try {
        const radiusOption = RADIUS_OPTIONS.find(r => r.miles === selectedRadius) || RADIUS_OPTIONS[2];
        const response = await fetch(
          `/api/google/places?lat=${latitude}&lng=${longitude}&category=${selectedCategory}&radius=${radiusOption.meters}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch nearby places');
        }

        const data: NearbyServicesResponse = await response.json();
        setPlaces(data.places || []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unknown error');
        setPlaces([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlaces();
  }, [latitude, longitude, selectedCategory, selectedRadius, open]);

  const handleNavigate = (place: Place) => {
    // Navigate directly with Google Maps (only option that supports waypoints)
    telegram.hapticFeedback('light');
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${place.lat},${place.lng}&travelmode=driving`, '_blank');
  };

  const categoryInfo = CATEGORIES.find(c => c.key === selectedCategory);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          border: `1px solid ${darkMode ? 'rgba(0, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
          maxHeight: '80vh',
          minHeight: '60vh',
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        pb: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ServicesIcon sx={{ color: '#06b6d4', fontSize: 28 }} />
          <Typography variant="h5" sx={{ color: darkMode ? '#fff' : '#111', fontWeight: 600 }}>
            {t('nearbyServicesTitle')}
          </Typography>
          {/* Radius Selector */}
          <FormControl size="small" sx={{ ml: 2, minWidth: 90 }}>
            <Select
              value={selectedRadius}
              onChange={(e) => setSelectedRadius(e.target.value as number)}
              sx={{
                color: darkMode ? '#fff' : '#111',
                backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                borderRadius: 2,
                fontSize: '0.9rem',
                fontWeight: 500,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#06b6d4',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#06b6d4',
                },
                '& .MuiSvgIcon-root': {
                  color: darkMode ? '#9ca3af' : '#666',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                  },
                },
              }}
            >
              {RADIUS_OPTIONS.map((option) => (
                <MenuItem 
                  key={option.miles} 
                  value={option.miles}
                  sx={{ 
                    color: darkMode ? '#fff' : '#111',
                    '&:hover': {
                      backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: darkMode ? 'rgba(6, 182, 212, 0.2)' : 'rgba(6, 182, 212, 0.1)',
                    },
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <IconButton onClick={onClose} sx={{ color: darkMode ? '#9ca3af' : '#666' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Category Chips */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            p: 2,
            flexWrap: 'wrap',
            justifyContent: 'center',
            borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          }}
        >
          {CATEGORIES.map(cat => (
            <Chip
              key={cat.key}
              icon={<cat.icon sx={{ fontSize: 20 }} />}
              label={t(cat.labelKey)}
              onClick={() => setSelectedCategory(cat.key)}
              sx={{
                backgroundColor: selectedCategory === cat.key 
                  ? cat.color 
                  : darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                color: selectedCategory === cat.key ? '#fff' : darkMode ? '#fff' : '#333',
                fontSize: '0.9rem',
                fontWeight: selectedCategory === cat.key ? 600 : 400,
                height: 36,
                px: 1,
                '& .MuiChip-icon': {
                  color: selectedCategory === cat.key ? '#fff' : cat.color,
                },
                '&:hover': {
                  backgroundColor: selectedCategory === cat.key 
                    ? cat.color 
                    : darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)',
                },
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </Box>

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 6, minHeight: 300 }}>
            <CircularProgress size={48} sx={{ color: categoryInfo?.color, mb: 2 }} />
            <Typography variant="body1" sx={{ color: darkMode ? '#9ca3af' : '#666' }}>
              {t('nearbyFindingPlaces', t(categoryInfo?.labelKey || 'nearbyGasStations').toLowerCase())}
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          </Box>
        ) : places.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body1" sx={{ color: darkMode ? '#9ca3af' : '#666' }}>
              {t('nearbyNoPlaces', t(categoryInfo?.labelKey || 'nearbyGasStations').toLowerCase())}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: '50vh', overflowY: 'auto' }}>
            {places.map((place, index) => (
              <ListItem
                key={place.id}
                sx={{
                  py: 1.5,
                  px: 3,
                  borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: categoryInfo?.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#fff',
                    }}
                  >
                    {index + 1}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: darkMode ? '#fff' : '#111',
                          fontWeight: 600,
                          fontSize: '1rem',
                        }}
                      >
                        {place.name}
                      </Typography>
                      {place.isOpen !== undefined && (
                        <Tooltip title={place.isOpen ? t('nearbyOpenNow') : t('nearbyClosed')}>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              backgroundColor: place.isOpen ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            {place.isOpen ? (
                              <OpenIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                            ) : (
                              <CloseIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                            )}
                            <Typography variant="caption" sx={{ color: place.isOpen ? '#22c55e' : '#ef4444', fontWeight: 500 }}>
                              {place.isOpen ? t('nearbyOpen') : t('nearbyClosed')}
                            </Typography>
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: categoryInfo?.color, fontWeight: 700, fontSize: '0.95rem' }}
                      >
                        {place.distanceDisplay}
                      </Typography>
                      {place.rating && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Rating
                            value={place.rating}
                            precision={0.1}
                            size="small"
                            readOnly
                          />
                          <Typography
                            variant="body2"
                            sx={{ color: darkMode ? '#9ca3af' : '#666', ml: 0.5 }}
                          >
                            ({place.totalRatings})
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title={t('nearbyGetDirections')}>
                    <IconButton
                      onClick={() => handleNavigate(place)}
                      sx={{ 
                        color: '#fff',
                        backgroundColor: categoryInfo?.color,
                        '&:hover': {
                          backgroundColor: categoryInfo?.color,
                          opacity: 0.8,
                        },
                      }}
                    >
                      <NavIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
